import { Button, cn } from '@iq/ui';
import { Maximize2Icon, Minimize2Icon, RotateCcwIcon, ZoomInIcon, ZoomOutIcon } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { KbImage } from './kb-image';

const MIN_SCALE = 0.5;
const MAX_SCALE = 5;
const SCALE_STEP = 1.25;
const WHEEL_FACTOR = 0.0015;
const DOUBLE_TAP_MS = 300;
const DOUBLE_TAP_PX = 30;
const DOUBLE_TAP_TARGET_SCALE = 2.5;

interface PanGesture {
  mode: 'pan';
  pointerId: number;
  startX: number;
  startY: number;
  baseX: number;
  baseY: number;
}

interface PinchGesture {
  mode: 'pinch';
  initialDistance: number;
  initialScale: number;
}

type Gesture = PanGesture | PinchGesture | null;

export interface ImageViewerProps {
  docId: string;
  imageRef: string;
  alt?: string;
  fullscreen?: boolean;
  onToggleFullscreen?: () => void;
  className?: string;
}

export function ImageViewer({
  docId,
  imageRef,
  alt,
  fullscreen = false,
  onToggleFullscreen,
  className,
}: ImageViewerProps) {
  const { t } = useTranslation();
  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const pointersRef = useRef(new Map<number, { x: number; y: number }>());
  const gestureRef = useRef<Gesture>(null);
  const lastTapRef = useRef<{ time: number; x: number; y: number } | null>(null);

  const clamp = (s: number) => Math.min(Math.max(s, MIN_SCALE), MAX_SCALE);

  // biome-ignore lint/correctness/useExhaustiveDependencies: clamp closes over only module constants (MIN/MAX_SCALE), so it's stable
  const zoomIn = useCallback(() => {
    setScale((s) => clamp(s * SCALE_STEP));
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: clamp closes over only module constants (MIN/MAX_SCALE), so it's stable
  const zoomOut = useCallback(() => {
    setScale((s) => {
      const next = clamp(s / SCALE_STEP);
      if (next <= 1) setOffset({ x: 0, y: 0 });
      return next;
    });
  }, []);

  const reset = useCallback(() => {
    setScale(1);
    setOffset({ x: 0, y: 0 });
  }, []);

  // Reset transforms when image source changes.
  useEffect(() => {
    setScale(1);
    setOffset({ x: 0, y: 0 });
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: clamp closes over only module constants (MIN/MAX_SCALE), so it's stable
  const onWheel = useCallback((e: React.WheelEvent<HTMLDivElement>) => {
    e.preventDefault();
    setScale((s) => {
      const next = clamp(s * Math.exp(-e.deltaY * WHEEL_FACTOR));
      if (next <= 1) setOffset({ x: 0, y: 0 });
      return next;
    });
  }, []);

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0 && e.pointerType === 'mouse') return;
    pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    e.currentTarget.setPointerCapture(e.pointerId);
    const count = pointersRef.current.size;
    if (count >= 2) {
      const pts = Array.from(pointersRef.current.values());
      const [a, b] = pts;
      if (a && b) {
        gestureRef.current = {
          mode: 'pinch',
          initialDistance: Math.hypot(b.x - a.x, b.y - a.y),
          initialScale: scale,
        };
      }
    } else if (scale > 1) {
      gestureRef.current = {
        mode: 'pan',
        pointerId: e.pointerId,
        startX: e.clientX,
        startY: e.clientY,
        baseX: offset.x,
        baseY: offset.y,
      };
    } else {
      gestureRef.current = null;
    }
  };

  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!pointersRef.current.has(e.pointerId)) return;
    pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const g = gestureRef.current;
    if (!g) return;
    if (g.mode === 'pan') {
      if (g.pointerId !== e.pointerId) return;
      setOffset({
        x: g.baseX + (e.clientX - g.startX),
        y: g.baseY + (e.clientY - g.startY),
      });
    } else if (g.mode === 'pinch') {
      const pts = Array.from(pointersRef.current.values());
      if (pts.length < 2) return;
      const [a, b] = pts;
      if (!a || !b) return;
      const dist = Math.hypot(b.x - a.x, b.y - a.y);
      if (g.initialDistance === 0) return;
      const next = clamp(g.initialScale * (dist / g.initialDistance));
      setScale(next);
      if (next <= 1) setOffset({ x: 0, y: 0 });
    }
  };

  const handleDoubleTap = (e: React.PointerEvent<HTMLDivElement>) => {
    const now = Date.now();
    const last = lastTapRef.current;
    if (
      last &&
      now - last.time < DOUBLE_TAP_MS &&
      Math.hypot(e.clientX - last.x, e.clientY - last.y) < DOUBLE_TAP_PX
    ) {
      // Double-tap detected — toggle between 100% and DOUBLE_TAP_TARGET_SCALE.
      if (scale > 1) {
        reset();
      } else {
        setScale(DOUBLE_TAP_TARGET_SCALE);
      }
      lastTapRef.current = null;
    } else {
      lastTapRef.current = { time: now, x: e.clientX, y: e.clientY };
    }
  };

  const onPointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    const wasActive = pointersRef.current.delete(e.pointerId);
    if (!wasActive) return;
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      // Pointer was never captured (already released by browser); ignore.
    }
    const remaining = pointersRef.current.size;
    if (remaining === 0) {
      const g = gestureRef.current;
      // Only treat as a tap if no pan/pinch was in flight that consumed movement.
      const isTap =
        g === null ||
        (g.mode === 'pan' &&
          Math.hypot(e.clientX - g.startX, e.clientY - g.startY) < DOUBLE_TAP_PX);
      gestureRef.current = null;
      if (isTap) handleDoubleTap(e);
    } else if (remaining === 1 && gestureRef.current?.mode === 'pinch') {
      // Pinch ended; downgrade to pan if the user keeps a finger down.
      const lastPointerEntry = Array.from(pointersRef.current.entries())[0];
      if (lastPointerEntry) {
        const [pointerId, p] = lastPointerEntry;
        gestureRef.current =
          scale > 1
            ? {
                mode: 'pan',
                pointerId,
                startX: p.x,
                startY: p.y,
                baseX: offset.x,
                baseY: offset.y,
              }
            : null;
      }
    }
  };

  // Keyboard shortcuts (active when canvas is focused).
  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === '+' || e.key === '=') {
      e.preventDefault();
      zoomIn();
    } else if (e.key === '-' || e.key === '_') {
      e.preventDefault();
      zoomOut();
    } else if (e.key === '0') {
      e.preventDefault();
      reset();
    }
  };

  const percent = Math.round(scale * 100);
  const canZoomIn = scale < MAX_SCALE - 0.001;
  const canZoomOut = scale > MIN_SCALE + 0.001;
  const isPanning = gestureRef.current?.mode === 'pan';

  return (
    <div
      className={cn(
        'grid h-full w-full grid-rows-[auto_minmax(0,1fr)] overflow-hidden bg-muted/20',
        className,
      )}
      data-testid="image-viewer"
    >
      <div className="flex items-center justify-between gap-1 border-b bg-background/95 px-2 py-1.5 backdrop-blur supports-[backdrop-filter]:bg-background/70">
        <div className="flex items-center gap-0.5 sm:gap-1">
          <Button
            type="button"
            size="icon"
            variant="ghost"
            className="size-9 sm:size-8"
            onClick={zoomOut}
            disabled={!canZoomOut}
            aria-label={t('chat.citation.zoomOut')}
            data-testid="image-viewer-zoom-out"
          >
            <ZoomOutIcon className="size-4" />
          </Button>
          <span
            className="min-w-10 px-1 text-center text-xs tabular-nums text-muted-foreground sm:min-w-12"
            data-testid="image-viewer-zoom-level"
          >
            {t('chat.citation.zoomLevel', { percent })}
          </span>
          <Button
            type="button"
            size="icon"
            variant="ghost"
            className="size-9 sm:size-8"
            onClick={zoomIn}
            disabled={!canZoomIn}
            aria-label={t('chat.citation.zoomIn')}
            data-testid="image-viewer-zoom-in"
          >
            <ZoomInIcon className="size-4" />
          </Button>
          <Button
            type="button"
            size="icon"
            variant="ghost"
            className="size-9 sm:size-8"
            onClick={reset}
            disabled={scale === 1 && offset.x === 0 && offset.y === 0}
            aria-label={t('chat.citation.resetZoom')}
            data-testid="image-viewer-reset"
          >
            <RotateCcwIcon className="size-4" />
          </Button>
        </div>
        {onToggleFullscreen ? (
          <Button
            type="button"
            size="icon"
            variant="ghost"
            className="size-9 sm:size-8"
            onClick={onToggleFullscreen}
            aria-label={t(
              fullscreen ? 'chat.citation.exitFullscreen' : 'chat.citation.enterFullscreen',
            )}
            data-testid="image-viewer-fullscreen"
          >
            {fullscreen ? (
              <Minimize2Icon className="size-4" />
            ) : (
              <Maximize2Icon className="size-4" />
            )}
          </Button>
        ) : null}
      </div>
      <div
        className={cn(
          'relative overflow-hidden select-none',
          scale > 1 ? 'cursor-grab active:cursor-grabbing' : 'cursor-default',
        )}
        style={{ touchAction: 'none' }}
        onWheel={onWheel}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onKeyDown={onKeyDown}
        role="img"
        aria-label={alt}
        data-testid="image-viewer-canvas"
      >
        <div
          className="absolute inset-0 flex items-center justify-center"
          style={{
            transform: `translate3d(${offset.x}px, ${offset.y}px, 0) scale(${scale})`,
            transformOrigin: 'center center',
            transition: isPanning ? 'none' : 'transform 120ms ease-out',
          }}
        >
          <KbImage
            docId={docId}
            imageRef={imageRef}
            {...(alt ? { alt } : {})}
            className="pointer-events-none max-h-full max-w-full object-contain shadow-sm"
          />
        </div>
      </div>
    </div>
  );
}
